import { defineStore } from 'pinia'
import type { Ecom } from '@serato/sws-sdk/types/Ecom'
import { CART_UUID_STORAGE_KEY } from '@/utils/const'
import type { BillingAddressFieldName } from '@/components/cards/billing-details-card/BillingDetailsForm.vue'

interface CartState {
  cartLoading: boolean
  cartValidationErrors: Map<BillingAddressFieldName, string>
  cartFetchAttempted: boolean,
  cart: Ecom.Cart | null
}

export const useCartStore = defineStore('CartStore', {
  state: (): CartState => ({
    cartLoading: false,
    cartValidationErrors: new Map(),
    cartFetchAttempted: false,
    cart: null
  }),
  getters: {
    items: (state) => state.cart?.items ?? [],
    totalPrice: (state) => (state.cart?.total_amount ?? 0).toFixed(2),
    subtotalAmount: (state) => (state.cart?.subtotal_amount ?? 0).toFixed(2),
    taxAmount: (state) => (state.cart?.tax_amount ?? 0).toFixed(2),
    taxRate: (state) => state.cart?.items[0].tax_rate ?? 0,
    currency: (state) => state.cart?.currency ?? '',
    billingAddress: (state) => state.cart?.billing_address ?? { country_code: 'US' },
    couponCode: (state) => state.cart?.coupon_code ?? '',
    /** Returns true if the cart is for a subscription */
    isSubscription: (state) => state.cart?.items.length === 1 && state.cart?.items[0].subscription_billing_period !== undefined,
    /** Shortcut to get the country **/
    billingAddressCountryCode: (state) => state.cart?.billing_address?.country_code ?? '',
    isBillingAddressCountryUS (): boolean {
      return this.billingAddressCountryCode === 'US'
    },
    isBillingAddressCountryUSorCA (): boolean {
      return ['US', 'CA'].includes(this.billingAddressCountryCode)
    },
    hasCartValidationErrors: (state) => [...state.cartValidationErrors.values()].some(errorMessage => errorMessage !== '')
  },
  actions: {
    setCart (cart: Ecom.Cart) {
      if (cart.items.length === 0) {
        this.clearCart()
        return
      }
      this.cart = cart
      localStorage.setItem(CART_UUID_STORAGE_KEY, cart.uuid)
    },
    clearCart () {
      this.cart = null
      localStorage.removeItem(CART_UUID_STORAGE_KEY)
    }
  }
})

